<template>
  <div class="catalogueNav">
    <!--
      El rail es el mismo marcado en los dos tamanos: en movil vive dentro de un
      <details> plegable y en escritorio el <summary> se oculta por CSS y el
      <details> queda siempre abierto. Se usa <details> nativo, y no el
      :focus-within de WorkPro, porque ese patron pierde el estado al hacer clic
      dentro y no es navegable por teclado.
    -->
    <details :open="isOpen" @toggle="onToggle">
      <summary>{{ $t('productCatalogue.exploreCategories') }}</summary>

      <h3>{{ $t('productCatalogue.categories') }}</h3>

      <CatalogueSkeleton v-if="isLoading" type="row"/>

      <div class="catalogueError" v-else-if="error">
        <p>{{ $t('productCatalogue.navError') }}</p>
        <a class="button accent" @click="$emit('retry')">
          <div class="text">{{ $t('productCatalogue.retry') }}</div>
        </a>
      </div>

      <div class="buttons packed vertical" ref="list" v-else>
        <template v-for="category in categories" :key="category.code">
          <LocalizedLink
              class="button catalogueNav__category"
              :class="{ catalogueNav__active: isActiveCategory(category) }"
              :to="categoryPath(category)"
              :aria-expanded="isActiveCategory(category) ? 'true' : 'false'"
          >
            <div class="text">{{ category.name }}</div>
            <div class="icon dropdown"></div>
          </LocalizedLink>

          <template v-if="isActiveCategory(category)">
            <LocalizedLink
                class="button catalogueNav__subcategory"
                :class="{ catalogueNav__active: isActiveSubCategory(subcategory) }"
                :to="subCategoryPath(category, subcategory)"
                v-for="subcategory in category.subcategories"
                :key="subcategory.code"
            >
              <div class="icon go"></div>
              <div class="text">{{ subcategory.name }}</div>
            </LocalizedLink>
          </template>
        </template>
      </div>
    </details>
  </div>
</template>

<script>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import LocalizedLink from '@/components/LocalizedLink.vue'
import CatalogueSkeleton from '@/components/catalogue/CatalogueSkeleton.vue'

const DESKTOP_QUERY = '(min-width: 1001px)'

export default {
  name: 'CatalogueNav',
  components: { LocalizedLink, CatalogueSkeleton },
  props: {
    categories: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    error: {
      type: Object,
      default: null
    },
    activeCategory: {
      type: String,
      default: null
    },
    activeSubCategory: {
      type: String,
      default: null
    }
  },
  emits: ['retry'],
  setup(props) {
    const { t } = useI18n()

    const isDesktop = ref(true)
    const isOpen = ref(true)
    const list = ref(null)

    const media = window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null

    // en escritorio siempre abierto; en movil solo si se esta viendo una categoria
    const syncOpen = () => {
      isDesktop.value = media ? media.matches : true
      isOpen.value = isDesktop.value || Boolean(props.activeCategory)
    }

    const onToggle = (event) => {
      isOpen.value = event.target.open
    }

    /*
     * El rail tiene el alto acotado y scroll propio, asi que con muchas
     * subcategorias el elemento activo puede quedar fuera del area visible: al
     * entrar en una subcategoria el arbol no ensenaria donde estas.
     *
     * Se desplaza lo justo para que se vea, no se centra: centrando la
     * subcategoria activa se empujaba el scroll al maximo y su categoria padre
     * salia de la vista, que es justo el contexto que hay que conservar (medido
     * en taladros: filas 3 y 22 de 32, y en la ventana caben 21).
     *
     * El scrollTop se calcula a mano en lugar de usar scrollIntoView porque este
     * ultimo desplaza tambien los ancestros y arrastraria la pagina entera al
     * cargar. El objetivo es el ultimo activo: la subcategoria si la hay, y si
     * no la categoria.
     */
    const revealActive = async () => {
      await nextTick()

      const container = list.value
      if (!container || container.scrollHeight <= container.clientHeight) return

      const actives = container.querySelectorAll('.catalogueNav__active')
      const target = actives[actives.length - 1]
      if (!target) return

      const top = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
      const bottom = top + target.offsetHeight
      // se deja el hueco de una fila por encima o por debajo, para que no quede pegado al borde
      const margin = target.offsetHeight

      if (top < container.scrollTop) {
        container.scrollTop = Math.max(0, top - margin)
        return
      }

      if (bottom > container.scrollTop + container.clientHeight) {
        container.scrollTop = bottom + margin - container.clientHeight
      }
    }

    onMounted(() => {
      syncOpen()
      revealActive()
      if (media) media.addEventListener('change', syncOpen)
    })

    onUnmounted(() => {
      if (media) media.removeEventListener('change', syncOpen)
    })

    watch(() => props.activeCategory, syncOpen)

    // el arbol llega despues del primer render, y el activo cambia al navegar
    watch(() => [props.categories, props.activeCategory, props.activeSubCategory], revealActive)

    /*
     * Los enlaces se construyen con la ruta traducida y no con
     * { name: 'Catalogue' }: navegar por nombre haria que vue-router usara
     * siempre el path principal (productos/...) e ignorara el alias del idioma,
     * dejando URLs como /en/productos/... tras entrar por /en/products.
     */
    const catalogueRoot = computed(() => t('routes.products'))

    const categoryPath = (category) => catalogueRoot.value + '/' + category.slug

    const subCategoryPath = (category, subcategory) =>
        catalogueRoot.value + '/' + category.slug + '/' + subcategory.slug

    const isActiveCategory = (category) => category.slug === props.activeCategory

    const isActiveSubCategory = (subcategory) => subcategory.slug === props.activeSubCategory

    return { isOpen, list, onToggle, categoryPath, subCategoryPath, isActiveCategory, isActiveSubCategory }
  }
}
</script>
